<script>
import { userService } from '@/shared/services/user.service';
import { mapGetters } from 'vuex';
import notificationService from '@/shared/services/notification.service';
import ListHeader from '@/components/common/ListHeader.vue';
import ImportUsersModal from '@/components/users/ImportUsersModal.vue';
import FilterChips from '@/components/common/FilterChips.vue';
import ListPagination from '@/components/common/ListPagination.vue';
import UserFilters from '@/components/users/UserFilters.vue';
import UserTable from '@/components/users/UserTable.vue';
import DeleteUserModal from '@/components/users/DeleteUserModal.vue';
import TemporaryPasswordModal from '@/components/users/TemporaryPasswordModal.vue';
import { hasPermission, PERMISSIONS, ROLES } from '@/constants/roles';
import {
  countActiveFilters,
  fromCriteria,
  toCriteria,
} from '@/shared/utils/list-filters';

/**
 * L'état de repos de chaque critère. Tout ce qui s'en écarte filtre, compte
 * dans la pastille du panneau, porte une pastille sous la barre, et part dans
 * le préréglage qu'on enregistre.
 */
const EMPTY_FILTERS = {
  searchQuery: '',
  roles: [],
  email: '',
  phone: '',
  hasPhone: '',
  accountState: '',
  hasSubmissions: '',
  dateAdded: { from: null, to: null },
  sort: 'recent',
};

// The users endpoint orders through order[field]/order[direction], the same
// pair the surveys list uses - it has never read a 'sortBy' parameter. Every
// field below is declared in the service's orderBuilders.
const SORTS = {
  recent: { field: 'createdAt', direction: 'desc' },
  idAsc: { field: 'id', direction: 'asc' },
  nameAsc: { field: 'lastName', direction: 'asc' },
};

const PRESENCE_LABELS = { yes: 'renseigné', no: 'non renseigné' };
const SUBMISSION_LABELS = { yes: 'au moins une', no: 'aucune' };
const ACCOUNT_STATE_LABELS = {
  active: 'Actif',
  pending: 'Activation en attente',
};

export default {
  name: 'ListUsers',
  components: {
    ListHeader,
    ImportUsersModal,
    FilterChips,
    ListPagination,
    UserFilters,
    UserTable,
    DeleteUserModal,
    TemporaryPasswordModal,
  },
  data() {
    return {
      users: [],
      loading: false,
      exporting: false,
      importOpen: false,
      error: null,
      activeTab: 'all',
      filterTimeout: null,
      filters: { ...EMPTY_FILTERS },
      appliedFilters: { ...EMPTY_FILTERS },
      panelOpen: false,
      appliedFilterId: null,
      // Rappeler un prereglage applique ses criteres tout de suite; la
      // temporisation de saisie n'a pas a relancer la meme recherche 400 ms
      // plus tard.
      skipNextSchedule: false,
      currentPage: 1,
      perPage: 10,
      totalUsers: 0,
      totalPages: 0,
      deleteModal: false,
      userToDelete: null,
      temporaryPasswordModal: false,
      userForTemporaryPassword: null,
    };
  },
  computed: {
    ...mapGetters('auth', { userRole: 'userRole', currentUser: 'user' }),

    /**
     * Seul un super administrateur distribue un mot de passe temporaire :
     * il part en clair par courriel et sa forme est connue d'avance. L'API
     * applique la même règle — ceci évite de proposer un bouton qui
     * reviendrait en 403.
     */
    canSendTemporaryPassword() {
      return this.userRole === ROLES.SUPER_ADMIN;
    },

    currentUserId() {
      return this.currentUser?.id ?? null;
    },

    canViewAllTabs() {
      return hasPermission(this.userRole, PERMISSIONS.VIEW_ADMINS);
    },

    tabs() {
      if (!this.canViewAllTabs) {
        return [{ key: 'Professional', label: 'Utilisateurs professionnels' }];
      }

      return [
        { key: 'all', label: 'Tous les utilisateurs' },
        { key: 'Professional', label: 'Professionnels' },
        { key: 'Admin', label: 'Admins' },
      ];
    },

    /**
     * Les rôles proposés au filtre. Un administrateur ne voit que des
     * professionnels — l'API le lui impose — donc lui proposer les autres
     * rôles ne ferait que promettre des résultats qui ne viendront pas.
     */
    roleOptions() {
      if (!this.canViewAllTabs) {
        return [{ value: ROLES.PROFESSIONAL, label: 'Professionnel' }];
      }

      return [
        { value: ROLES.SUPER_ADMIN, label: 'Super administrateur' },
        { value: ROLES.ADMIN, label: 'Administrateur' },
        { value: ROLES.PROFESSIONAL, label: 'Professionnel' },
      ];
    },

    activeCount() {
      return countActiveFilters(this.filters, EMPTY_FILTERS);
    },

    /** Ce qu'on enregistre sous un nom: les critères qui filtrent, et rien d'autre. */
    criteria() {
      return toCriteria(this.filters, EMPTY_FILTERS);
    },

    chips() {
      const out = [];
      const f = this.appliedFilters;

      if (f.searchQuery) {
        out.push({ key: 'searchQuery', label: `Recherche : ${f.searchQuery}` });
      }
      if (f.roles?.length) {
        const labels = f.roles.map(
          (role) =>
            this.roleOptions.find((option) => option.value === role)?.label ??
            role,
        );
        out.push({ key: 'roles', label: `Rôles : ${labels.join(', ')}` });
      }
      if (f.email) {
        out.push({ key: 'email', label: `Email : ${f.email}` });
      }
      if (f.phone) {
        out.push({ key: 'phone', label: `Téléphone : ${f.phone}` });
      }
      if (f.hasPhone) {
        out.push({
          key: 'hasPhone',
          label: `Téléphone ${PRESENCE_LABELS[f.hasPhone]}`,
        });
      }
      if (f.accountState) {
        out.push({
          key: 'accountState',
          label: `État : ${ACCOUNT_STATE_LABELS[f.accountState]}`,
        });
      }
      if (f.hasSubmissions) {
        out.push({
          key: 'hasSubmissions',
          label: `Enquêtes : ${SUBMISSION_LABELS[f.hasSubmissions]}`,
        });
      }
      if (f.dateAdded?.from || f.dateAdded?.to) {
        const from = f.dateAdded.from;
        const to = f.dateAdded.to;
        const label =
          from && to
            ? `du ${from} au ${to}`
            : from
              ? `depuis le ${from}`
              : `jusqu'au ${to}`;

        out.push({ key: 'dateAdded', label: `Ajout ${label}` });
      }

      return out;
    },

    hasFilters() {
      return this.chips.length > 0;
    },
  },
  mounted() {
    if (!this.canViewAllTabs) {
      this.activeTab = 'Professional';
    }

    this.fetchUsers();
  },
  beforeUnmount() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  },
  methods: {
    /** Les critères actifs, traduits en paramètres que l'API lit. */
    queryParams() {
      const f = this.appliedFilters;

      const params = {
        searchText: f.searchQuery || undefined,
        tab: this.activeTab !== 'all' ? this.activeTab : undefined,
        roles: f.roles?.length ? f.roles : undefined,
        email: f.email || undefined,
        phone: f.phone || undefined,
        hasPhone: f.hasPhone ? f.hasPhone === 'yes' : undefined,
        hasSubmissions: f.hasSubmissions
          ? f.hasSubmissions === 'yes'
          : undefined,
        accountState: f.accountState || undefined,
        createdAtFrom: f.dateAdded?.from || undefined,
        createdAtTo: f.dateAdded?.to || undefined,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });

      return params;
    },

    async fetchUsers() {
      this.loading = true;
      this.error = null;

      try {
        const sort = SORTS[this.appliedFilters.sort] ?? SORTS.recent;

        const response = await userService.getUsers({
          page: this.currentPage,
          limit: this.perPage,
          ...this.queryParams(),
          'order[field]': sort.field,
          'order[direction]': sort.direction,
        });

        if (response?.data) {
          this.users = Array.isArray(response.data) ? response.data : [];
          this.totalUsers = response.meta?.total || response.total || 0;
          this.totalPages =
            response.meta?.totalPages ||
            response.totalPages ||
            Math.ceil(this.totalUsers / this.perPage);
        } else if (Array.isArray(response)) {
          this.users = response;
          this.totalUsers = response.length;
          this.totalPages = 1;
        } else {
          this.users = [];
          this.totalUsers = 0;
          this.totalPages = 0;
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        this.error =
          error.response?.data?.message ||
          'Erreur lors du chargement des utilisateurs.';
        this.users = [];
        this.totalUsers = 0;
        this.totalPages = 0;
      } finally {
        this.loading = false;
      }
    },

    // Filters apply on their own - the chips are what tells the user what is
    // active, so there is no "Filtrer" button to forget to press.
    scheduleFetch() {
      if (this.skipNextSchedule) {
        this.skipNextSchedule = false;
        return;
      }

      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
      }

      // Une main sur les criteres s'ecarte du prereglage rappele: la liste
      // deroulante ne doit plus pretendre le montrer.
      this.appliedFilterId = null;

      this.filterTimeout = setTimeout(() => {
        this.applyNow();
      }, 400);
    },

    applyNow() {
      this.appliedFilters = { ...this.filters };
      this.currentPage = 1;
      this.fetchUsers();
    },

    /** Rappelle un préréglage: ses critères sont reposés, la liste se relance. */
    applySavedFilter(filter) {
      if (!filter) {
        return;
      }

      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
      }

      this.skipNextSchedule = true;
      this.filters = fromCriteria(filter.criteria, EMPTY_FILTERS);
      this.appliedFilterId = filter.id;
      this.panelOpen = countActiveFilters(this.filters, EMPTY_FILTERS) > 0;
      this.applyNow();
    },

    /**
     * Le préréglage marqué par défaut s'applique à l'ouverture, mais jamais
     * par-dessus des critères déjà réglés à la main.
     */
    onSavedFiltersLoaded(defaultFilter) {
      if (defaultFilter && this.activeCount === 0) {
        this.applySavedFilter(defaultFilter);
      }
    },

    changeTab(key) {
      this.activeTab = key;
      this.currentPage = 1;
      this.fetchUsers();
    },

    clearFilter(key) {
      this.skipNextSchedule = true;
      this.filters = { ...this.filters, [key]: EMPTY_FILTERS[key] };
      this.appliedFilterId = null;
      this.applyNow();
    },

    clearAllFilters() {
      this.skipNextSchedule = true;
      this.filters = { ...EMPTY_FILTERS };
      this.appliedFilterId = null;
      this.applyNow();
    },

    changePage(page) {
      this.currentPage = page;
      this.fetchUsers();
    },

    goToCreateUser() {
      this.$router.push({ name: 'create-user' });
    },

    editUser(userId) {
      this.$router.push({ name: 'update-user', params: { id: userId } });
    },

    confirmDelete(user) {
      this.userToDelete = user;
      this.deleteModal = true;
    },

    confirmTemporaryPassword(user) {
      this.userForTemporaryPassword = user;
      this.temporaryPasswordModal = true;
    },

    onTemporaryPasswordSent() {
      // La ligne change d'état — « Mot de passe temporaire » — donc on la
      // relit plutôt que de la retoucher de mémoire.
      this.fetchUsers();
      this.userForTemporaryPassword = null;
      this.temporaryPasswordModal = false;
    },

    onUserDeleted() {
      if (this.users.length === 1 && this.currentPage > 1) {
        this.currentPage -= 1;
      }

      this.fetchUsers();
      this.userToDelete = null;
      this.deleteModal = false;
    },

    async exportUsers() {
      if (this.exporting) {
        return;
      }

      this.exporting = true;

      try {
        const blob = await userService.exportUsers(this.queryParams());

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `utilisateurs_${new Date().getTime()}.xlsx`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        notificationService.toastError(
          "Une erreur est survenue lors de l'exportation.",
        );
      } finally {
        this.exporting = false;
      }
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.scheduleFetch();
      },
    },
  },
};
</script>

<template>
  <div class="user-list-container mt-4">
    <div class="list-card">
      <ListHeader :tabs="tabs" :active-tab="activeTab" @tab-change="changeTab">
        <template #actions>
          <button
            type="button"
            class="list-btn list-btn--primary"
            @click="goToCreateUser"
          >
            <i class="ri-add-line"></i>
            Ajouter un utilisateur
          </button>
          <button
            type="button"
            class="list-btn list-btn--ghost"
            title="Importer des utilisateurs depuis un fichier"
            @click="importOpen = true"
          >
            <i class="ri-upload-2-line"></i>
            Importer
          </button>
          <button
            type="button"
            class="list-btn list-btn--ghost"
            title="Exporter la liste des utilisateurs"
            :disabled="exporting"
            @click="exportUsers"
          >
            <i class="ri-file-excel-line"></i>
            {{ exporting ? 'Export…' : 'Exporter' }}
          </button>
        </template>
      </ListHeader>

      <UserFilters
        v-model="filters"
        v-model:expanded="panelOpen"
        :role-options="roleOptions"
        :active-count="activeCount"
        :criteria="criteria"
        :applied-filter-id="appliedFilterId"
        @reset="clearAllFilters"
        @apply-saved="applySavedFilter"
        @saved-filters-loaded="onSavedFiltersLoaded"
      />

      <FilterChips
        :chips="chips"
        @clear="clearFilter"
        @clear-all="clearAllFilters"
      />

      <UserTable
        :users="users"
        :loading="loading"
        :error="error"
        :has-filters="hasFilters"
        :can-send-temporary-password="canSendTemporaryPassword"
        :current-user-id="currentUserId"
        @edit="editUser"
        @delete="confirmDelete"
        @temporary-password="confirmTemporaryPassword"
        @clear-filters="clearAllFilters"
      />

      <ListPagination
        :current-page="currentPage"
        :per-page="perPage"
        :total="totalUsers"
        :total-pages="totalPages"
        noun="utilisateurs"
        @page-change="changePage"
      />
    </div>

    <DeleteUserModal
      v-model="deleteModal"
      :user="userToDelete"
      @deleted="onUserDeleted"
    />

    <TemporaryPasswordModal
      v-model="temporaryPasswordModal"
      :user="userForTemporaryPassword"
      @sent="onTemporaryPasswordSent"
    />

    <ImportUsersModal v-model="importOpen" @imported="fetchUsers" />
  </div>
</template>
